import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import "../Styles/Header.css";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LaunchIcon from "@mui/icons-material/Launch";
import NetworkStatus from "./NetworkStatus";

function Header() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [role, setRole] = useState(null);
  const [notificationsCount, setNotificationsCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const location = useLocation();

  const isMobile = () => window.innerWidth < 768;

  const closeSidebar = () => {
    if (isMobile()) {
      setSidebarOpen(false);
      const sidebar = document.querySelector(".sidebar");
      if (sidebar) {
        sidebar.classList.remove("active");
      }
    }
  };

  useEffect(() => {
    closeSidebar();
  }, [location.pathname]);

  useEffect(() => {
    const storedRole = localStorage.getItem("userRole");
    setRole(storedRole);
    fetchNotifications(storedRole);
    fetchProfileImage();
  }, []);

  const fetchProfileImage = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await axios.get(
        "https://api.uniums.com/api/profile-image",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.imageUrl) {
        setProfileImage(response.data.imageUrl);
      }
    } catch (error) {
      console.error("Error fetching profile image:", error);
    }
  };

  // Update the fetchNotifications function to properly filter already read notifications
  const fetchNotifications = async (storedRole) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const requests = [];
      const userId = localStorage.getItem("userId");

      // Fetch regular notifications
      requests.push(
        axios.get(
          "https://api.uniums.com/api/notifications/notifications-user",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        )
      );

      // Fetch role-specific leave notifications
      if (storedRole === "admin") {
        requests.push(
          axios.get(
            "https://api.uniums.com/api/faculty/admin-leave-notifications",
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          )
        );
        requests.push(
          axios.get(
            "https://api.uniums.com/api/student/student-admin-leave-notifications",
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          )
        );
      } else if (storedRole === "faculty") {
        requests.push(
          axios.get(
            "https://api.uniums.com/api/faculty/faculty-leave-notifications",
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          )
        );
      } else if (storedRole === "student") {
        requests.push(
          axios.get(
            "https://api.uniums.com/api/student/student-leave-notifications",
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          )
        );
      }

      const responses = await Promise.all(requests);

      // Process all notifications
      let allNotifications =  [];

      // Process regular notifications from the first response
      const regularNotifications = responses[0].data.notifications || [];

      // Process leave notifications from the remaining responses
      const leaveNotifications = responses
        .slice(1)
        .flatMap((response) => response.data.notifications || []);

      // Combine all notifications
      allNotifications = [...regularNotifications, ...leaveNotifications];

      // Get unread notifications
      const unreadNotifications = allNotifications.filter((notification) => {
        // For regular notifications with isRead array
        if (notification.isRead && Array.isArray(notification.isRead)) {
          const userReadStatus = notification.isRead.find(
            (item) => item.userId === userId
          );
          return userReadStatus ? !userReadStatus.read : true;
        }
        // For leave notifications that might have different structure
        return !notification.clicked;
      });

      // Count unread notifications
      const unreadCount = unreadNotifications.length;

      // Store all notifications but mark which ones are unread
      const processedNotifications = allNotifications.map((notification) => {
        let isUnread = false;

        // For regular notifications with isRead array
        if (notification.isRead && Array.isArray(notification.isRead)) {
          const userReadStatus = notification.isRead.find(
            (item) => item.userId === userId
          );
          isUnread = userReadStatus ? !userReadStatus.read : true;
        } else {
          // For leave notifications
          isUnread = !notification.clicked;
        }

        return {
          ...notification,
          isUnread,
        };
      });

      // Sort to show unread notifications first
      processedNotifications.sort((a, b) => {
        if (a.isUnread && !b.isUnread) return -1;
        if (!a.isUnread && b.isUnread) return 1;

        // If both have same read status, sort by date (newest first)
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      setNotifications(processedNotifications);
      setNotificationsCount(unreadCount);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  };

  const toggleSidebar = () => {
    const sidebar = document.querySelector(".sidebar");
    sidebar.classList.toggle("active");
    setSidebarOpen(!sidebarOpen);
  };

  const handleBellClick = async () => {
    setIsModalOpen(true);

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const storedRole = localStorage.getItem("userRole");
      const userId = localStorage.getItem("userId");

      // Only make API calls if there are unread notifications
      if (notificationsCount > 0) {
        // Mark role-specific leave notifications as clicked
        if (storedRole === "faculty" || storedRole === "student") {
          // Prepare the API endpoint based on the role
          let apiEndpoint = "";
          if (storedRole === "faculty") {
            apiEndpoint =
              "https://api.uniums.com/api/faculty/faculty-leave-notifications/mark-clicked";
          } else if (storedRole === "student") {
            apiEndpoint =
              "https://api.uniums.com/api/student/student-leave-notifications/mark-clicked";
          }

          try {
            // Make a PUT request to mark all unclicked leave notifications as clicked
            await axios.put(
              apiEndpoint,
              {}, // No request body needed
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
          } catch (error) {
            console.error(
              "Error marking leave notifications as clicked:",
              error
            );
          }
        }

        try {
          // Mark regular notifications as read
          await axios.put(
            "https://api.uniums.com/api/notifications/mark-read",
            {}, // No request body needed
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        } catch (error) {
          console.error("Error marking notifications as read:", error);
        }

        // Update the notifications state to reflect that they are now read/clicked
        const updatedNotifications = notifications.map((notification) => {
          // For all notification types, mark as read
          return {
            ...notification,
            isUnread: false,
            // Update isRead array for regular notifications
            ...(notification.isRead && Array.isArray(notification.isRead)
              ? {
                  isRead: notification.isRead.map((readObj) =>
                    readObj.userId === userId
                      ? { ...readObj, read: true }
                      : readObj
                  ),
                }
              : {}),
            // Update clicked status for leave notifications
            clicked: true,
          };
        });

        setNotifications(updatedNotifications);
        setNotificationsCount(0); // Reset notification count
      }
    } catch (err) {
      console.error("Error handling bell click:", err);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const renderIcons = () => {
    if (role === "admin" || role === "faculty" || role === "student") {
      return (
        <>
          <NetworkStatus />
          <div className="bell-container" onClick={handleBellClick}>
            <NotificationsIcon className="bell-icon" />
            {notificationsCount > 0 && (
              <span className="notification-count">{notificationsCount}</span>
            )}
          </div>
          <Link to="/profile">
            {profileImage ? (
              <img
                src={profileImage}
                alt="Profile"
                className="header-profile-image"
                onError={() => setProfileImage(null)}
              />
            ) : (
              <AccountCircleIcon className="profile-icon" />
            )}
          </Link>
        </>
      );
    }

    if (role === "super admin") {
      return (
        <>
          <NotificationsIcon className="bell-icon" />
          <AccountCircleIcon className="profile-icon" />
        </>
      );
    }

    return null;
  };

  return (
    <header className="navbar">
      <div className="navbar-logo">
        {sidebarOpen ? (
          <CloseIcon onClick={toggleSidebar} className="menu-icon" />
        ) : (
          <MenuIcon onClick={toggleSidebar} className="menu-icon" />
        )}
        <Link className="unihrm">UNIUMS</Link>
      </div>

      <div className="navbar-actions">{renderIcons()}</div>

      {isModalOpen && (
        <NotificationModal notifications={notifications} onClose={closeModal} />
      )}
    </header>
  );
}

function NotificationModal({ notifications, onClose }) {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const toggleDropdown = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  // Determine the base URL for each role
  const storedRole = localStorage.getItem("userRole");
  const getBaseUrl = () => {
    if (storedRole === "student") {
      return "/student-leaves-history";
    } else if (storedRole === "admin") {
      return "/leave-request";
    } else if (storedRole === "faculty") {
      return "/faculty-leaves-history";
    }
    return "";
  };

  // Helper function to determine if a notification is a leave request
  const isLeaveRequest = (notification) => {
    return (
      notification.hasOwnProperty("name") &&
      notification.hasOwnProperty("reason")
    );
  };

  return (
    <div className="modal_overlay_notification">
      <div className="modal_notification">
        <div className="modal_header_notification">
          <div className="notify_head">
            <h2>Notifications</h2>
          </div>
          <div className="close_btn_notification" onClick={onClose}>
            <CloseIcon />
          </div>
        </div>
        <ul className="notification-list">
          {notifications.length === 0 ? (
            <li className="notification-item">
              <p>No notifications available</p>
            </li>
          ) : (
            notifications.map((notification, index) => (
              <li
                key={index}
                className={`notification-item ${
                  notification.isUnread ? "unread-notification" : ""
                }`}
                onClick={() => toggleDropdown(index)}
              >
                <div className="notification-header">
                  <p className="icon_color">
                    <NotificationsIcon />
                  </p>
                  {isLeaveRequest(notification) ? (
                    <p>
                      <strong>Leave Request From </strong> {notification.name}
                    </p>
                  ) : (
                    <p>
                      <strong>{notification.title || "Notification"}</strong>
                    </p>
                  )}
                  <p className="view-details-link">
                    {isLeaveRequest(notification) ? (
                      <Link to={getBaseUrl()} onClick={onClose}>
                        <LaunchIcon />
                      </Link>
                    ) : (
                      <span>{/* No icon for regular notifications */}</span>
                    )}
                  </p>
                  <p className="notification-timestamp">
                    {new Date(notification.createdAt).toLocaleDateString(
                      "en-GB"
                    )}{" "}
                    &nbsp;||&nbsp;&nbsp;
                    {new Date(notification.createdAt).toLocaleTimeString(
                      "en-US",
                      {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      }
                    )}
                  </p>
                </div>
                {expandedIndex === index && (
                  <div className="notification-dropdown">
                    {isLeaveRequest(notification) ? (
                      <p>
                        <strong>Reason:</strong> {notification.reason}
                      </p>
                    ) : (
                      <p>{notification.message || "No additional details"}</p>
                    )}
                  </div>
                )}
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}

export default Header;
